import { FC } from 'react';
import {
  Box,
  BoxProps,
  Button,
  Collapse,
  Divider,
  Flex,
  Heading,
  VStack,
} from '@chakra-ui/react';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import { de } from 'date-fns/locale';
import { CommentForm } from '../Forms/CommentForm';
import { CommentWithChildren } from '../Paragraph/CommentListAndFormular/CommentListAndFormular';

type CommentProps = BoxProps & {
  data: CommentWithChildren;
  onClickAnswer: (id: number) => void;
  openCommentIdForm: number | null;
};

interface NestedCommentsProps {
  data: CommentWithChildren[];
  onClickAnswer: (id: number) => void;
  openCommentIdForm: number | null;
}

const NestedComments: FC<NestedCommentsProps> = ({
  data,
  onClickAnswer,
  openCommentIdForm,
}) => {
  if (!data.length) return null;

  return (
    <VStack
      pt={6}
      mt={6}
      spacing={6}
      divider={<Divider borderColor="gray.200" opacity={1} />}
      ml={9}
      borderTop="1px solid"
      borderTopColor="gray.100"
    >
      {data.map((comment) => (
        <Comment
          data={comment}
          key={comment.id}
          onClickAnswer={onClickAnswer}
          openCommentIdForm={openCommentIdForm}
        />
      ))}
    </VStack>
  );
};

export const Comment: FC<CommentProps> = ({
  data,
  onClickAnswer,
  openCommentIdForm,
  ...props
}) => {
  const isFormOpen = openCommentIdForm === data.id;

  return (
    <Box width="100%" key={data.id} {...props}>
      <Flex justifyContent="space-between">
        <Box fontWeight="bold">{data.username || 'Gast'}</Box>
        <Box fontSize="sm">
          {format(fromUnixTime(data.created), 'dd MMMM yyyy', {
            locale: de,
          })}
        </Box>
      </Flex>

      <Box
        mt={4}
        dangerouslySetInnerHTML={{
          __html: data.comment,
        }}
      />

      <Collapse in={isFormOpen} animateOpacity>
        <Box
          bg="gray.50"
          p={6}
          mt={9}
          border="1px solid"
          borderColor="gray.400"
        >
          <Heading
            fontSize="xl"
            mb={4}
            fontWeight={500}
            as="h3"
            color="primary"
          >
            Auf den Kommentar von {data.username || 'Gast'} antworten
          </Heading>

          <CommentForm parentCommentId={data.id} />
        </Box>
      </Collapse>
      <Collapse in={!isFormOpen} animateOpacity>
        <Flex justifyContent="flex-end">
          <Button
            size="sm"
            mt={12}
            onClick={() => {
              onClickAnswer(data.id);
            }}
          >
            Antworten
          </Button>
        </Flex>
      </Collapse>

      <NestedComments
        data={data.children}
        onClickAnswer={onClickAnswer}
        openCommentIdForm={openCommentIdForm}
      />
    </Box>
  );
};
